<template>
  <v-row>
    <v-col cols="12">
      <Bar v-if="currentChartData.datasets?.length > 0" :data="currentChartData"
           :options="{ indexAxis: 'x' }"/>
    </v-col>
    <v-col cols="12" class="text-center">
      <v-btn v-if="!showAll" @click="showAll = true" variant="outlined">Show All</v-btn>
      <v-btn v-else @click="showAll = false" variant="outlined">Show Top 10</v-btn>
    </v-col>

  </v-row>
</template>
<script setup lang="ts">

import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip
} from "chart.js";
import {Bar} from "vue-chartjs";
import {computed, ref} from "vue";
import {useI18n} from "vue-i18n";

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, ArcElement);

interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string[];
  }[];
}

interface ReportDataValue {
  [key: string]: Record<string, number>;
}

const {t} = useI18n();

const props = defineProps(
  {
    curriculum: {
      type: String,
      required: true,
    },
    reportData: {
      type: Object,
      required: true,
    },
    electiveGroup: {
      type: String,
      default: "all"
    },
  }
);

const colors = [
  "#003f5c",
];

const showAll = ref(false);

/**
 * Merges all the electives chosen into a single data set and distribution table
 */
const mergedElectivesChartData = computed(() => {
  if (!props.reportData || !props.curriculum) return {labels: [], datasets: []};

  const electiveKeys: Record<string, string[]> = {
    "national_senior_certificate": ["elective_1", "elective_2", "elective_3", "elective_4", "elective_5"],
    "cambridge": ["group_3", "group_4", "group_5", "ungrouped"],
    "international_baccalaureate": ["group_3", "group_4", "group_6"],
    "pre_international_baccalaureate": ["group_3", "group_4", "group_6"],
    "kenyan_certificate_of_secondary_education": ["elective_1", "elective_2"],
  };

  const reportDataObject: ReportDataValue = props.reportData as ReportDataValue;

  const mergedElectives: Record<string, number> = {};

  const applicableElectives = props.electiveGroup === "all"
    ? electiveKeys[props.curriculum]
    : [props.electiveGroup];

  for (const elective in reportDataObject) {
    if (!applicableElectives.includes(elective))
      continue;

    const electiveObject = reportDataObject[elective];
    for (const subject in electiveObject) {
      if (mergedElectives[subject] !== undefined) {
        mergedElectives[subject] += electiveObject[subject];
      } else {
        mergedElectives[subject] = electiveObject[subject];
      }
    }
  }

  const sortedEntries = Object.entries(mergedElectives).sort((a, b) => b[1] - a[1]);
  const sortedObject = Object.fromEntries(sortedEntries);

  const labels = Object.keys(sortedObject)
    .map((label) => t(`curriculums.${props.curriculum}.subjects.${label}`));

  const dataValues = Object.values(sortedObject);

  const data: ChartData = {
    labels,
    datasets: [
      {
        label: "Electives",
        backgroundColor: colors,
        data: dataValues,
      },
    ],
  };

  return data;
});

const currentChartData = computed(() => {
  if (showAll.value) {
    return mergedElectivesChartData.value;
  }

  const limitedLabels = mergedElectivesChartData.value.labels.slice(0, 10);
  const limitedData = mergedElectivesChartData.value.datasets[0].data.slice(0, 10);

  return {
    ...mergedElectivesChartData.value,
    labels: limitedLabels,
    datasets: [
      {
        ...mergedElectivesChartData.value.datasets[0],
        data: limitedData,
      },
    ],
  };
});

</script>
<style scoped>

</style>
